<template>
  <div class="flex w-full relative bg-gray-50 dark:bg-navy-1000">
    <SiteScanSidebar
      @select="selectSideBarOption"
    />
    <!-- ACTIVE SITE SCAN PAGE -->
    <div
      id="siteScanScrollable"
      class="flex flex-col w-full bg-gray-50 dark:bg-navy-1000"
      ref="scrollable"
    >
      <!-- SITE SCAN HEADER -->
      <div class="flex-col items-center flex">
        <div
          class="sectionHeader flex items-center justify-center w-full z-20 lg:z-15 lg:transition-all duration-200 transform"
          :class="headerScrolled"
        >
          <SiteScanLogo class="transition-all duration-200" :class="logoClass" />
        </div>
      </div>
      <!-- SELECTED SITE COMMAND COMPONENT -->
      <div :class="pageClass" v-if="readyToLoad">
        <ChemicalPage v-if="path === '/SiteScan/Chemicals/Config'" />
        <!--<FaultsPage v-else-if="path === '/SiteScan/Faults'" class="px-5"/>
        <InspectionPage v-else-if="path === '/SiteScan/Inspection'" class="px-5"/>
        <SecurityPage v-else-if="path === '/SiteScan/Security'" class="px-5"/>-->
        <PackageConfiguratorPage v-else-if="path === '/SiteScan/Chemicals/Packages'" class="md:px-5" />
        <ChemicalDashboard v-else-if="chemicalAccess" />
        <h2 v-else class="py-32 mx-auto">
          Please choose an option from the sidebar.
        </h2>
      </div>
      <div v-else class="w-full h-full mt-3" :class="pageClass">
        <LoadingSpinner class="mt-52" />
      </div>
    </div>
  </div>
</template>

<script>
import { defineAsyncComponent } from "vue"
import { mapState, mapGetters, mapMutations, mapActions } from "vuex"
import SiteScanSidebar from "../../Site-Scan/components/SiteScanSidebar.vue"
//import FaultsPage from './FaultsPage.vue'
//import InspectionPage from './InspectionPage.vue'
//import SecurityPage from './SecurityPage.vue'
import LoadingSpinner from "@/components/Loading/LoadingSpinner/LoadingSpinner.vue"
import LoadingSpinnerPage from "@/components/Loading/LoadingSpinner/LoadingSpinnerPage.vue"
import SiteScanLogo from "@/assets/Cruz_Control_Site_Scan_Logo_Full_Color.svg"
import { getAllSiteChemicalData, getAllChemTargetsGlobal, getChemSiteConfig } from "../services/Chemical"
import { getAllEmployees, getAllTeams } from "../../Site-Track/services/ManagerService"
import { getAllAssets } from "../../Site-Track/services/Assets"
import { getVendors } from "../../Site-Track/services/PurchaseOrders"
import { getImagesBySources } from "@/services/images.service"
import PackageConfiguratorPage from './PackageConfiguratorPage.vue'

export default {
  name: "SiteScan",
  components: {
    PackageConfiguratorPage,
    ChemicalDashboard: defineAsyncComponent({
      loader: () => import("../../Site-Scan/views/ChemicalDashboard.vue"),
      loadingComponent: LoadingSpinnerPage,
    }),
    ChemicalPage: defineAsyncComponent({
      loader: () => import("./ChemicalPage.vue"),
      loadingComponent: LoadingSpinnerPage,
    }),
    //SecurityPage,
    //InspectionPage,
    //FaultsPage,
    SiteScanSidebar,
    LoadingSpinner,
    SiteScanLogo
  },
  data() {
    return {
      teams: null
    }
  },
  computed: {
    ...mapState([
      "headers",
      "screen",
      "orientation",
      "refreshed"
    ]),
    ...mapGetters([
      "showingModuleHeader",
      "atTopOfPage",
      "getSiteId",
      "fullEmployeeData",
      "fullTeamsData",
      "siteAssets",
      "getEmployeeSites",
      "getEmployeesUserId",
      "siteSuppliers",
      "loadedGlobalValues",
      "getIsTenantSuperuser",
      "getUserManagementAccess",
      "getPermissionSet",
      "getUserPermissions"
    ]),
    chemicalAccess() {
      if (this.getIsTenantSuperuser) return true
      return this.getUserPermissions.site_scan.chemicals.dashboard?.main_view?.object?.read_bool
    },
    headerScrolled() {
      if (this.isMobileLandscape && !this.isTablet && !this.screen.xl) return "hidden"
      let className = "md:fixed lg:shadow-md bg-gray-50 dark:bg-navy-1000 lg:bg-white-off "
      if (this.atTopOfPage || !this.screen.lg) {
        className += "h-16 lg:h-24 "
      }
      else className += "shadow-lg h-24 lg:h-16 "
      if (!this.showingModuleHeader && this.screen.lg) className += "-translate-y-full md:translate-y-0 "
      else className += "-translate-y-0 "
      return className
    },
    pageClass() {
      let classes = "flex flex-col relative "
      if (!this.isMobileLandscape || (this.isTablet)) classes += "md:mt-16 lg:mt-24 "
      return classes
    },
    path() {
      return this.$route.path
    },
    logoClass() {
      if (this.atTopOfPage && this.screen.lg) return "h-14 lg:h-20 py-2 my-0"
      else return "h-14 lg:h-16 py-2" 
    },
    isMobile() {
      return /Mobi/i.test(window.navigator.userAgent)
    },
    isMobileLandscape() {
      return this.isMobile && this.orientation.landscape
    },
    isTablet() {
      return /ipad|tablet/i.test(window.navigator.userAgent.toLowerCase())
    },
    needLoad() {
      // MARK IF PATH REQUIRES STATE VALUES PRELOAD
      return {
        // "/SiteScan/Chemicals/Config": [
        //   "assets",
        //   "employee_sites",
        //   "suppliers",
        //   "allEmployeesData",
        //   "allTeamsData"
        // ]
      }
    },
    readyToLoad() {
      if (!this.needLoad[this.path]) return true
      for (let needToLoad of this.needLoad[this.path]) {
        if (!this.loadedGlobalValues[needToLoad]) return false
      }
      return true
    }
  },
  watch: {
    path(val) {
      this.setSTPage(val)
    },
    refreshed(val) {
      if (val) {
        this.onLoadSequence()
        this.refreshData(false)
      }
    },
  },
  async mounted() {
    this.showModuleHeader(true)
    this.onLoadSequence()
    this.loadAssets()
    this.loadVendors()
    await this.loadEmployees()
    this.loadTeams()
  },
  methods: {
    ...mapMutations([
      "setSTPage",
      "showModuleHeader",
      "setPrompt",
      "setSiteScanLoaded",
      "setSiteScanLoadError",
      "setActiveContainers",
      "setChemGroups",
      "setConsumableCategories",
      "setChemFormulas",
      "setChemSensors",
      "setChemicals",
      "setGlobalTanks",
      "setInactiveContainers",
      "setAllEmployeeAvatars",
      "refreshData",
    ]),
    ...mapActions([
      "setFullEmployeeData",
      "setFullTeamsData",
      "setSiteAssets",
      "setAssetModels",
      "setLocations",
      "setSuppliers",
    ]),
    selectSideBarOption(option) {
      this.setSTPage(option)
    },
    async onLoadSequence() {
      this.setSiteScanLoaded(false)
      await this.loadChemicalMonitoringData()
      this.loadChemTargetsGlobal()
      this.setSiteScanLoaded(true)
    },
    loadChemicalMonitoringData() {
      this.showLoadError = false
      return new Promise(resolve =>
        getAllSiteChemicalData(this.getSiteId, this.headers)
          .then(async response => {
            console.log(response)
            if (response.status === 200 && response.data.results) {
              let activeContainers = response.data.results.active_containers
              activeContainers.map(x => {
                let fill = this.clamp((x.last_sensor_data?.calculated_value / x.sensor_formula?.max_result) * 100, 0, 100)
                if (isNaN(fill)) fill = 0
                x.fill = fill
              })
              let inactiveContainers = response.data.results.inactive_containers
              activeContainers.map(x => {
                let fill = this.clamp((x.last_sensor_data?.calculated_value / x.sensor_formula?.max_result) * 100, 0, 100)
                if (isNaN(fill)) fill = 0
                x.fill = fill
              })
              this.setActiveContainers(activeContainers)
              this.setInactiveContainers(inactiveContainers)
              this.setChemGroups(response.data.results.groups)
              this.setConsumableCategories(response.data.results.consumable_categories)
              this.setChemFormulas(response.data.results.formulas)
              this.setChemSensors(response.data.results.sensors)
              this.setChemicals(response.data.results.chemicals)
              // await this.$nextTick()
              // this.filterOptions.selectors["sensor_formula.id"].options = this.chemFormulaOptions
              // this.filterOptions.selectors["sensor_formula.id"].database = this.chemFormulaDatabase
              // this.filterOptions.selectors["sensor_group.id"].options = this.chemGroupOptions
              // this.filterOptions.selectors["sensor_group.id"].database = this.chemGroupDatabase
              
            }
            else {
              console.log(response)
              this.setSiteScanLoadError(response)
              this.setPrompt(["Unable to load all chemical data.", "closePrompt", null, { header: "Error" }])
              if (response.status === 403) {
                this.setPrompt(["You do not have the required permissions to access sensor targets.", "closePrompt", null, { header: "Error" }])
              }
            }
            this.loading = false
            resolve(response?.status)
          }).catch(error => {
            console.log(error)
            this.setPrompt(["Unable to load all chemical data.", "closePrompt", null, { header: "Error" }])
            this.setSiteScanLoadError(error)
            this.loading = false
            resolve(null)
          }
        )
      )
    },
    async loadChemTargetsGlobal() {
      await getAllChemTargetsGlobal(this.headers)
      .then(response => {
        if (response.status === 200) {
          this.setGlobalTanks(response.data?.results?.sensor_targets)
        }
      })
    },
    loadEmployees() {
      let employeeAccess = this.getIsTenantSuperuser
        ||
        // will need to replace with new employee endpoint not just for managers
        this.getUserPermissions.site_track?.manager_tools?.employee_management?.main_view?.object?.read_bool
      if (!employeeAccess) {
        this.setFullEmployeeData([])
        return
      }
      getAllEmployees(this.headers)
      .then(response => {
        if (response?.status === 200) {
          this.setFullEmployeeData(response.data.results)
          this.loadAllEmployeeAvatars(response.data.results)
          if (this.teams) this.matchEmployeesToTeams()
        }
        else {
          console.log(response)
          if (response.status === 403) {
            this.setPrompt(["You do not have the required permissions to access employee data.", "closePrompt", null, { header: "Error" }])
          }
          else this.setPrompt(["Could not load all data for this site!", "getPreloadData", null, { header: "Error" }])
        }
      }, (error) => {
        console.log(error)
        this.setPrompt(["Could not load all data for this site!", "getPreloadData", null, { header: "Error" }])
      })
    },
    loadAllEmployeeAvatars(employees) {
      if (Object.keys(this.getEmployeesUserId).length === 0) return
      let userIds = []
      for (let user of employees) {
        if (user.user_id) userIds.push(user.user_id)
      }
      getImagesBySources(userIds, this.headers)
      .then(response => {
        if (response.status === 200) {
          this.setAllEmployeeAvatars(response.data.results)
        }
        else console.log(response)
      }).catch(error => console.log(error))
    },
    loadTeams() {
      const teamAccess = this.getIsTenantSuperuser
        || 
        this.getUserPermissions.site_track?.manager_tools?.team_management?.main_view?.object?.read_bool
        ||
        this.getUserPermissions.site_scan?.chemicals?.configuration?.main_view?.object?.read_bool
      if (!teamAccess) {
        return
      }
      getAllTeams(this.headers)
      .then(async response => {
        if (response?.status === 200) {
          this.teams = response.data.results
          this.matchEmployeesToTeams(response.data.results)
        }
        else {
          console.log(response)
          if (response.status === 403) {
            this.setPrompt(["You do not have the required permissions to access team data.", "closePrompt", null, { header: "Error" }])
          }
          else this.setPrompt(["Could not load all data for this site!", "getPreloadData", null, { header: "Error" }])
        }
      }, (error) => {
        console.log(error)
        this.setPrompt(["Could not load all data for this site!", "getPreloadData", null, { header: "Error" }])
      })
    },
    matchEmployeesToTeams() {
      for (let team of this.teams) {
        let employees = this.fullEmployeeData.filter(employee => {
          employee.site_team_id === team.id
          && employee.active
          && !employee.hidden
        })
        team.employees = employees
      }
      this.setFullTeamsData(this.teams)
    },
    loadAssets() {
      const assetAccess = 
        this.getIsTenantSuperuser
        || 
        this.getUserPermissions.site_track?.assets.assets?.main_view?.object?.read_bool
        ||
        this.getUserPermissions.site_scan?.chemicals?.configuration?.main_view?.object?.read_bool
      if (!assetAccess) {
        this.setSiteAssets([])
        return
      }
      getAllAssets(this.headers)
      .then(response => {
        if (response.status === 200) {
          this.setSiteAssets(response.data.results)
        }
        else {
          console.log(response)
          if (response.status === 403) {
            this.setPrompt(["You do not have the required permissions to access asset data.", "closePrompt", null, { header: "Error" }])
          }
          else this.setPrompt(["Could not load all data for this site!", "loadAssets", null, { header: "Error" }])
        }
      }).catch(error => {
        console.log(error)
        this.setPrompt(["Could not load all data for this site!", "loadAssets", null, { header: "Error" }])
      })
    },
    loadVendors() {
      const vendorAccess = 
        this.getIsTenantSuperuser
        || 
        this.getUserPermissions.site_track?.purchasing.purchase_orders?.main_view?.object?.read_bool
        ||
        this.getUserPermissions.site_scan?.chemicals?.configuration?.main_view?.object?.read_bool
      if (!vendorAccess) {
        this.setSuppliers([])
        return
      }
      getVendors(this.headers)
      .then(response => {
        if (response.status === 200) {
          this.setSuppliers(response.data.results)
        }
        else {
          console.log(response)
          if (response.status === 403) {
            this.setPrompt(["You do not have the required permissions to access vendor data.", "closePrompt", null, { header: "Error" }])
          }
          else this.setPrompt(["Could not load all data for this site!", "loadVendors", null, { header: "Error" }])
        }
      }).catch(error => {
        console.log(error)
        this.setPrompt(["Could not load all data for this site!", "loadVendors", null, { header: "Error" }])
      })
    },
    clamp(num, min, max) {
      if (!num || isNaN(num)) num === 0
      return parseInt(Math.min(Math.max(num, min), max))
    }
  },
};
</script>